/** @jsxImportSource @emotion/react */
import { Expression, Map as MBMap, CircleLayer } from 'mapbox-gl'
import { byAssetIdIn } from '../RiskMapView.utilities'
import { MapLayerStyle } from '@app/types/mapTypes'

export interface PointLayerProps {
  map: MBMap
  layerStyle: MapLayerStyle
  id: string
  beforeId: string
  idAttribute?: string
  isVisible: boolean
  sourceLayer: string
  vulnerabilityData?: Map<string, number>
  vulnerabilityPalette?: string[]
  colorExpression?: Expression
  fillOpacityExpression?: Expression
  assetIds?: { [asset_id: number]: boolean }
  sortKeyExpression?: Expression
  hasFilters?: boolean
  shouldUpdate?: boolean
}

/**
 * Creates a point layer that is displayed as circles.
 * Styling is proivded either via colorExpression or with
 * vulnerabilityData & vulnerabilityPalette.
 */
export const addPointLayer = ({
  map,
  layerStyle,
  id,
  beforeId,
  idAttribute,
  isVisible,
  sourceLayer,
  vulnerabilityData,
  vulnerabilityPalette,
  colorExpression,
  fillOpacityExpression,
  assetIds,
  sortKeyExpression,
  hasFilters = false,
  shouldUpdate = false,
}: PointLayerProps) => {
  const pointLayer: CircleLayer = {
    id,
    type: 'circle',
    source: id,
    'source-layer': sourceLayer,
    layout: {
      visibility: isVisible ? 'visible' : 'none',
      'circle-sort-key': sortKeyExpression || 0,
      ...layerStyle.layout,
    },
    paint: {
      'circle-opacity': fillOpacityExpression,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 4, 15, 4, 16, 8],
      'circle-color': colorExpression || [
        'to-color',
        [
          'at',
          ['coalesce', ['feature-state', 'vulnerability_style'], 0],
          ['literal', vulnerabilityPalette],
        ],
      ],
      ...layerStyle.paint,
    },
  }
  if (shouldUpdate) {
    if (pointLayer.paint) {
      for (const paintProp of Object.entries(pointLayer.paint)) {
        map.setPaintProperty(id, paintProp[0] as any, paintProp[1])
      }
    }
    if (pointLayer.layout) {
      for (const layoutProp of Object.entries(pointLayer.layout)) {
        map.setLayoutProperty(id, layoutProp[0] as any, layoutProp[1])
      }
    }
    if (!hasFilters && !assetIds) map.setFilter(id, null)
  } else {
    map.addSource(id, {
      type: 'vector',
      url: `mapbox://${id}`,
      ...(idAttribute && { promoteId: idAttribute }),
    })
    map.addLayer(pointLayer, beforeId)
  }
  if (hasFilters && assetIds) {
    const visibilityExpression: Expression = ['boolean', byAssetIdIn(assetIds)]
    map.setFilter(id, visibilityExpression)
  }

  // Replace feature state:
  map.removeFeatureState({
    source: id,
    sourceLayer: sourceLayer,
  })
  if (vulnerabilityData) {
    for (const [assetId, vulnerabilityStyle] of vulnerabilityData) {
      map.setFeatureState(
        {
          source: id,
          sourceLayer: sourceLayer,
          id: parseInt(assetId),
        },
        {
          vulnerability_style: vulnerabilityStyle,
        },
      )
    }
  }
}
